import { useEffect, useState } from 'react'
import { Modal } from '@/shared/components/Modal'
import { HclSnippet } from '@/shared/components/HclSnippet'
import { CopyOverlay } from '@/shared/components/CopyOverlay'
import { useProviderConsume } from '@/lib/hooks'
import { authorizationHeader } from '@/lib/api'

interface Props {
  orgId: string
  /** Registry the artefact belongs to ('terraform.io' | 'opentofu.org') - part of its identity. */
  upstream: string
  namespace: string
  type: string
  /** Exact version to pin the consume config to. */
  version: string
  /** terramantle's own build of `version` (`<version>-0.0.N`), when one is published.
   *  When present it is what the config pins - we never offer the upstream pin. */
  patchedVersion?: string | null
  onClose: () => void
}

/**
 * "Consume this version". Renders the consume config for a pinned provider version: the
 * version-pinned `required_providers` block, the org's registry endpoint URL, and the `tofu init`
 * / .terraform.lock.hcl CLI steps - all computed by the worker
 * (GET .../:upstream/:namespace/:type/:version/consume) so the source address + host follow the one
 * established registry convention. `servable` from the worker says whether `tofu init` would
 * resolve this exact version right now (serve mode, bytes, constraint, entitlement).
 */
export function ProviderCodeModal({ orgId, upstream, namespace, type, version, patchedVersion, onClose }: Props) {
  // Pin the patched build whenever there is one - it is the only version we offer.
  const pinned = patchedVersion || version
  const consume = useProviderConsume(orgId, upstream, namespace, type, pinned)
  // The signed-in user's access token, substituted into the auth step's `$TOKEN` placeholder so
  // the export line is copy-paste ready. The DISPLAYED command stays masked (a JWT is ~800 chars,
  // and a live credential shouldn't sit readable on screen) - only the copied value carries it.
  const [accessToken, setAccessToken] = useState<string | null>(null)
  useEffect(() => {
    authorizationHeader()
      .then((h) => setAccessToken(h.replace(/^Bearer /, '') || null))
      .catch(() => setAccessToken(null))
  }, [])

  function withToken(command: string, mode: 'copy' | 'display'): string {
    if (!command.includes('$TOKEN') || !accessToken) return command
    return mode === 'copy'
      ? command.replace('$TOKEN', accessToken)
      : command.replace('$TOKEN', `${accessToken.slice(0, 12)}…`)
  }

  const data = consume.data

  return (
    <Modal track="provider_consume_code" title="Consume this version" kicker={`// ${namespace}/${type} @ ${pinned}`} maxWidth={560} onClose={onClose}>
      {consume.loading ? (
        <p style={s.desc}>Loading consume config…</p>
      ) : consume.error ? (
        <div className="feedback-error">{consume.error}</div>
      ) : !data ? (
        <p style={s.desc}>No config available.</p>
      ) : (
        <>
          <p style={s.desc}>
            Add this to your Terraform configuration to consume <code style={s.inline}>{namespace}/{type}</code>{' '}
            pinned to <code style={s.inline}>{pinned}</code> from your Terramantle registry
            (<code style={s.inline}>{data.registry_endpoint_url}</code>):
          </p>
          <div style={{ marginBottom: 16 }}>
            <HclSnippet code={data.required_providers_hcl ?? ''} />
          </div>

          {data.servable === false && (
            <div style={s.warn}>
              This version is not servable right now - the provider is set to Off or Block, the version is
              outside its constraint, or it has not been cached yet - so{' '}
              <code style={s.inline}>tofu init</code> may not resolve it until that changes.
            </div>
          )}

          <p style={s.desc}>Then run:</p>
          {(data.steps ?? []).map((step, i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <div style={s.stepTitle}>
                <span style={s.stepNum}>{i + 1}</span>
                {step.title}
              </div>
              <div style={s.codeBlock}>
                <pre style={s.pre}>{withToken(step.command, 'display')}</pre>
                <CopyOverlay value={withToken(step.command, 'copy')} label="Copy" />
              </div>
              {step.note && <p style={s.note}>{step.note}</p>}
            </div>
          ))}
        </>
      )}
    </Modal>
  )
}

const s: Record<string, React.CSSProperties> = {
  desc: {
    fontFamily: 'var(--body)',
    fontSize: '0.85rem',
    color: 'var(--text2)',
    margin: '0 0 16px 0',
    lineHeight: 1.5,
  },
  inline: {
    fontFamily: 'var(--mono)',
    fontSize: '0.8rem',
    color: 'var(--text)',
    background: 'var(--bg3)',
    padding: '2px 6px',
    borderRadius: 3,
  },
  codeBlock: {
    position: 'relative',
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    overflow: 'hidden',
  },
  pre: {
    fontFamily: 'var(--mono)',
    fontSize: '0.8rem',
    color: 'var(--text)',
    margin: 0,
    padding: '16px',
    overflow: 'auto',
    lineHeight: 1.5,
  },
  stepTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: 'var(--body)',
    fontSize: '0.8rem',
    color: 'var(--text2)',
    marginBottom: 8,
  },
  stepNum: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    fontFamily: 'var(--mono)',
    fontSize: '0.7rem',
    color: 'var(--text)',
    flexShrink: 0,
  },
  note: {
    fontFamily: 'var(--body)',
    fontSize: '0.75rem',
    color: 'var(--text3)',
    margin: '6px 0 0 0',
    lineHeight: 1.5,
  },
  warn: {
    padding: '10px 14px',
    marginBottom: 16,
    background: 'var(--amber-d)',
    border: '1px solid rgba(245,158,11,0.2)',
    borderRadius: 'var(--radius)',
    color: 'var(--amber)',
    fontSize: '0.8rem',
    lineHeight: 1.5,
  },
}
